import gql from 'graphql-tag';
import { defaultDataIdFromObject } from 'apollo-cache-inmemory';

import { groupPublicFragment } from './group';
import { RaceEntryFragment } from './raceEntry';

const UserNotificationFragment = gql`
  fragment UserNotificationFragment on User {
    notifications {
      id
      entityId
      timestamp
      status
      action {
        __typename
        ... on SimpleNotificationAction {
          message
          thumbnail {
            uri
            alt
          }
          kind
          type
          permalink
          relativePermalink
          actor {
            givenName
            familyName
          }
        }
        ... on ContentChangeSuggestionApprovedNotification {
          event {
            id
            title
          }
          contentChange {
            id
            type
          }
        }
      }
    }
  }
`;

const UserPublicBasicFragment = gql`
  ${groupPublicFragment}
  fragment UserPublicBasicFragment on User {
    id: primaryIdentityId
    primaryIdentityId
    displayName
    givenName
    familyName
    picture: pictureUrl
    pictureUrl
    tagline
    interests {
      id
      sportId
      distanceId
    }
    location {
      addressSummary
      center {
        latitude
        longitude
      }
    }
    groups {
      ...groupPublicFragment
    }
  }
`;

export const UserNotificationQuery = gql`
  query UserNotificationQuery($primaryIdentityId: String!) {
    user: userByPrimaryIdentityId(primaryIdentityId: $primaryIdentityId) {
      id: primaryIdentityId
      ...UserNotificationFragment
    }
  }
  ${UserNotificationFragment}
`;

export const UserQueryPublicBasic = gql`
  query UserQueryPublicBasic($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserPublicBasicFragment
    }
  }
  ${UserPublicBasicFragment}
`;

export const UserPublicBasicAndBookingsQuery = gql`
  query UserPublicBasicAndBookingsQuery($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserPublicBasicFragment
      identityIds
      emailAddress
      loginsCount
      creditCodes
      bookings {
        ticket {
          id
          event {
            id
          }
        }
      }
    }
  }
  ${UserPublicBasicFragment}
`;

export const UsersQueryPublicBasic = gql`
  query UsersQueryPublicBasic($ids: [String!]!) {
    profiles: usersByPrimaryIdentityIds(primaryIdentityIds: $ids) {
      ...UserPublicBasicFragment
    }
  }
  ${UserPublicBasicFragment}
`;

export const UserQueryPrivate = gql`
  query UserQueryPrivate($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserPublicBasicFragment
      identityIds
      emailAddress
      loginsCount
      creditCodes
      permissions {
        employeePermissions
        organiserPermissions {
          id
          permissions
        }
      }
    }
  }
  ${UserPublicBasicFragment}
`;

export const UserQueryEmailAddress = gql`
  query UserQueryEmailAddress($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      id
      emailAddress
    }
  }
`;

export const UserQueryEmailPreferences = gql`
  query UserQueryEmailPreferences($emailAddress: String!) {
    emailPreferences(emailAddress: $emailAddress) {
      disabled
      emailAddress
      unsubscribed
    }
  }
`;

export const EmailPreferencesIdQuery = gql`
  query EmailPreferencesIdQuery($emailAddress: String!) {
    emailPreferences(emailAddress: $emailAddress) {
      id
    }
  }
`;

export const SaveUserEmailPreferences = gql`
  mutation SaveUserEmailPreferences(
    $emailAddress: String!
    $disabled: Boolean
    $unsubscribed: [EmailUnsubscribeType]
    $acquisitionChannel: EmailAcquisitionChannelType
    $anonymousId: String
    $userId: String
  ) {
    upsertEmailPreferences(
      input: {
        emailAddress: $emailAddress
        disabled: $disabled
        unsubscribed: $unsubscribed
        acquisitionChannel: $acquisitionChannel
      }
      trackingParams: { anonymousId: $anonymousId, userId: $userId }
    ) {
      disabled
      emailAddress
      unsubscribed
      acquisitionChannel
    }
  }
`;
export const CaptureEmailAddress = gql`
  mutation CaptureEmailAddress(
    $emailAddress: String!
    $acquisitionChannel: EmailAcquisitionChannelType!
    $anonymousId: String
    $userId: String
    $firstName: String
    $isOptedIn: Boolean
  ) {
    captureEmailAddress(
      input: {
        emailAddress: $emailAddress
        acquisitionChannel: $acquisitionChannel
        isOptedIn: $isOptedIn
        firstName: $firstName
      }
      trackingParams: { anonymousId: $anonymousId, userId: $userId }
    ) {
      id
    }
  }
`;
export const UnsubscribeFromEmailType = gql`
  mutation UnsubscribeFromEmailType(
    $emailAddress: String!
    $emailType: EmailUnsubscribeType!
  ) {
    unsubscribeFromEmailType(
      input: { emailAddress: $emailAddress, emailType: $emailType }
    ) {
      id
    }
  }
`;

const SavedEventFragment = gql`
  fragment SavedEventFragment on Event {
    id
    images {
      hero {
        medium
      }
    }
    location {
      addressSummary
    }
    startDate
    title
  }
`;

const UserSavedEventFragment = gql`
  fragment UserSavedEventFragment on UserSavedEvent {
    id
    createdAt
    raceAt
    event {
      ...SavedEventFragment
    }
  }
  ${SavedEventFragment}
`;

export const UserQueryPublicSavedEvents = gql`
  query UserQueryPublicSavedEvents($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      id: primaryIdentityId
      primaryIdentityId
      savedEvents {
        ...UserSavedEventFragment
      }
    }
  }
  ${UserSavedEventFragment}
`;

export const UserQueryReviews = gql`
  query UserQueryReviews($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      id: primaryIdentityId
      reviewCount
      reviews {
        id
        images
        race {
          id
          title
          event {
            id
            title
            slug
          }
        }
        year
        review
        createdAt
        ratings {
          atmosphere
          course
          organisation
          overall
        }
        photos {
          id
          likes {
            primaryIdentityId
            user {
              id: primaryIdentityId
              primaryIdentityId
            }
          }
          uri
        }
        tags
        reactions {
          id
          likes {
            id
            user {
              id: primaryIdentityId
              primaryIdentityId
            }
          }
        }
        event {
          id
          title
          images {
            hero {
              medium
            }
          }
        }
      }
    }
  }
`;

const UserOnboardingFragment = gql`
  fragment UserOnboardingFragment on User {
    id: primaryIdentityId
    displayName
    givenName
    familyName
    tagline
    emailAddress
    location {
      addressSummary
      center {
        latitude
        longitude
      }
    }
  }
`;
export const UserPrivateQueryOnboarding = gql`
  query UserPrivateQueryOboarding($id: String!) {
    user: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserOnboardingFragment
    }
  }
  ${UserOnboardingFragment}
`;

export const UpdateProfileOnboardingMutation = gql`
  mutation updateProfileOnboardingMutation(
    $primaryIdentityId: String!
    $givenName: String
    $familyName: String
    $emailAddress: String
    $location: UserLocationInput
    $tagline: String
  ) {
    updateUser(
      input: {
        primaryIdentityId: $primaryIdentityId
        givenName: $givenName
        familyName: $familyName
        emailAddress: $emailAddress
        location: $location
        tagline: $tagline
      }
    ) {
      ...UserOnboardingFragment
    }
  }
  ${UserOnboardingFragment}
`;
export const UserQueryPublicSavedExternalBookings = gql`
  query UserQueryWithSavedExternalBookings($id: String!) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      ...UserPublicBasicFragment
      savedExternalBookings {
        race {
          id
          event {
            id
          }
        }
      }
      bookings {
        ticket {
          race {
            id
            event {
              id
            }
          }
        }
        isPaymentAuthorized
      }
    }
  }
  ${UserPublicBasicFragment}
`;

export const UpdateProfileMutation = gql`
  mutation updateProfileMutation(
    $primaryIdentityId: String!
    $givenName: String
    $familyName: String
    $emailAddress: String
    $location: UserLocationInput
    $profilePictureFile: Upload
    $tagline: String
  ) {
    updateUser(
      input: {
        primaryIdentityId: $primaryIdentityId
        givenName: $givenName
        familyName: $familyName
        emailAddress: $emailAddress
        location: $location
        profilePictureFile: $profilePictureFile
        tagline: $tagline
      }
    ) {
      ...UserPublicBasicFragment
      givenName
      familyName
      identityIds
      emailAddress
      loginsCount
      location {
        addressSummary
        center {
          latitude
          longitude
        }
      }
    }
  }
  ${UserPublicBasicFragment}
`;

export const SaveEventMutation = gql`
  mutation saveEventMutation($id: ID!) {
    saveEvent(input: { eventId: $id }) {
      id: primaryIdentityId
      savedEvents {
        ...UserSavedEventFragment
      }
    }
  }
  ${UserSavedEventFragment}
`;

export const getSaveEventMutationOptimisticResponse = (
  gqlClient,
  eventId,
  user = {},
) => {
  const id = defaultDataIdFromObject({ id: eventId, __typename: 'Event' });
  const defaultEvent = {
    __typename: 'Event',
    id: eventId,
  };
  let event;
  try {
    event = gqlClient.readFragment({
      id,
      fragment: SavedEventFragment,
    });
  } catch (_) {
    event = defaultEvent;
  }
  if (!event) {
    event = defaultEvent;
  }

  return {
    saveEvent: {
      id: user.primaryIdentityId,
      savedEvents: (user.savedEvents || []).concat({
        __typename: 'UserSavedEvent',
        id: '123',
        createdAt: new Date().toISOString(),
        event,
        raceAt: new Date().toISOString(),
      }),
      __typename: 'User',
    },
  };
};

export const UnsaveEventMutation = gql`
  mutation unsaveEventMutation($id: ID!) {
    unsaveEvent(input: { eventId: $id }) {
      id: primaryIdentityId
      savedEvents {
        id
        ...UserSavedEventFragment
      }
    }
  }
  ${UserSavedEventFragment}
`;

export const getUnsaveEventMutationOptimisticResponse = (
  eventId,
  user = {},
) => ({
  unsaveEvent: {
    id: user.primaryIdentityId,
    savedEvents: (user.savedEvents || []).filter(
      (savedEvent) => savedEvent.event.id !== eventId,
    ),
    __typename: 'User',
  },
});

export const SaveExternalBookingMutation = gql`
  mutation saveExternalBookingMutation($eventId: ID!, $raceId: ID!) {
    saveExternalBooking(input: { eventId: $eventId, raceId: $raceId }) {
      id: primaryIdentityId
      savedExternalBookings {
        id
        race {
          id
          event {
            id
          }
        }
      }
    }
  }
`;

export const getSaveExternalBookingMutationOptimisticResponse = (
  eventId,
  raceId,
  user = {},
) => ({
  saveExternalBooking: {
    id: user.primaryIdentityId,
    savedExternalBookings: (user.savedExternalBookings || []).concat({
      __typename: 'UserSavedExternalBooking',
      id: '123',
      race: {
        id: raceId,
        event: {
          __typename: 'Event',
          id: eventId,
        },
        __typename: 'Race',
      },
    }),
    __typename: 'User',
  },
});

export const UnsaveExternalBookingMutation = gql`
  mutation unsaveExternalBookingMutation($eventId: ID!, $raceId: ID!) {
    unsaveExternalBooking(input: { eventId: $eventId, raceId: $raceId }) {
      id: primaryIdentityId
      savedExternalBookings {
        id
        race {
          id
          event {
            id
          }
        }
      }
    }
  }
`;

export const getUnsaveExternalBookingMutationOptimisticResponse = (
  eventId,
  raceId,
  user = {},
) => ({
  unsaveExternalBooking: {
    id: user.primaryIdentityId,
    savedExternalBookings: (user.savedExternalBookings || []).filter(
      (savedExternalBooking) =>
        !(
          savedExternalBooking.race.event.id === eventId &&
          savedExternalBooking.race.id === raceId
        ),
    ),
    __typename: 'User',
  },
});

export const LikeActivityMutation = gql`
  mutation likeActivityMutation(
    $activityId: ID!
    $activityType: ActivityType!
    $activityPrimaryIdentityId: ID!
    $eventId: ID!
  ) {
    likeActivity(
      input: {
        activityId: $activityId
        activityType: $activityType
        activityPrimaryIdentityId: $activityPrimaryIdentityId
        eventId: $eventId
      }
    ) {
      id
      likes {
        id
        user {
          id: primaryIdentityId
          primaryIdentityId
        }
      }
    }
  }
`;

export const CommentOnActivityMutation = gql`
  mutation commentOnActivityMutation(
    $activityId: ID!
    $activityType: ActivityType!
    $activityPrimaryIdentityId: ID!
    $eventId: ID!
    $text: String!
  ) {
    commentOnActivity(
      input: {
        activityId: $activityId
        activityType: $activityType
        activityPrimaryIdentityId: $activityPrimaryIdentityId
        eventId: $eventId
        text: $text
      }
    ) {
      id
      comments {
        id
        text
        user {
          id: primaryIdentityId
          displayName
          primaryIdentityId
          pictureUrl
        }
        createdAt
      }
    }
  }
`;

export const getLikeActivityMutationOptimisticResponse = (
  reactions,
  primaryIdentityId,
) => {
  const primaryIdentityIds = [].concat(primaryIdentityId);

  return {
    likeActivity: {
      id: reactions.id,
      isOptimisticResponse: true,
      likes: (reactions.likes || []).concat(
        primaryIdentityIds.map((userId) => {
          return {
            id: `${Date.now()}_${userId}`,
            user: {
              id: userId,
              primaryIdentityId: userId,
              __typename: 'User',
            },
            __typename: 'ActivityLike',
          };
        }),
      ),
      __typename: 'ActivityReaction',
    },
  };
};

export const UnlikeActivityMutation = gql`
  mutation unlikeActivityMutation(
    $activityId: ID!
    $activityType: ActivityType!
    $activityPrimaryIdentityId: ID!
    $eventId: ID!
  ) {
    unlikeActivity(
      input: {
        activityId: $activityId
        activityType: $activityType
        activityPrimaryIdentityId: $activityPrimaryIdentityId
        eventId: $eventId
      }
    ) {
      id
      likes {
        id
        user {
          id: primaryIdentityId
          primaryIdentityId
        }
      }
    }
  }
`;

export const getUnlikeActivityMutationOptimisticResponse = (
  reactions,
  primaryIdentityId,
) => ({
  unlikeActivity: {
    id: reactions.id,
    likes: reactions.likes.filter(
      (l) => l.user.primaryIdentityId !== primaryIdentityId,
    ),
    __typename: 'ActivityReaction',
  },
});

// todo check is it used
// eslint-disable-next-line no-unused-vars
const SavedSeachFragment = `
  {
    id
    sportId
    distanceId
    location {
      ... on CenteredLocation {
        addressSummary
        radiusKm
        center {
          latitude
          longitude
        }
      }
      ... on ViewportLocation {
        addressSummary
        northEast {
          latitude
          longitude
        }
        southWest {
          latitude
          longitude
        }
      }
    }
  }
`;

export const CreateEventQuestionMutation = gql`
  mutation CreateEventQuestionMutation(
    $primaryIdentityId: ID!
    $text: String!
    $eventId: String!
  ) {
    createQuestion(
      input: {
        primaryIdentityId: $primaryIdentityId
        text: $text
        eventId: $eventId
      }
    ) {
      id
    }
  }
`;

export const SaveUserInterestsMutation = gql`
  mutation SaveUserInterestsMutation($interests: [UserSaveInterestInput!]!) {
    saveInterests(input: $interests) {
      id
      interests {
        id
        sportId
        distanceId
      }
    }
  }
`;

export const UnsaveUserInterestsMutation = gql`
  mutation UnsaveUserInterestsMutation(
    $interests: [UserUnsaveInterestInput!]!
  ) {
    unsaveInterests(input: $interests) {
      id
      interests {
        id
        sportId
        distanceId
      }
    }
  }
`;

export const AllUserRaceEntriesQuery = gql`
  query AllUserRaceEntriesQuery($id: String!, $limit: Int) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      id
      raceEntries(limit: $limit) {
        id
        status
        bookingId
        numberOfParticipants
        eventSummary {
          eventId
          title
          raceId
          raceStartDate
          raceTitle
          raceType
          submissionWindow {
            startDate
            endDate
          }
        }
        participants {
          firstName
          lastName
          primaryIdentityId
        }
        information {
          key
          value
        }
        year
        event {
          id
          images {
            hero {
              medium
            }
          }
          place {
            address {
              city
              countryShort
            }
          }
          virtualEventDetails {
            isVirtual
          }
          title
          races {
            races {
              id
              information {
                sport
                distance
                metricLength
                raceUnit
                raceLength
              }
              title
            }
          }
        }
      }
    }
  }
`;
export const UserRaceEntriesQuery = gql`
  query UserRaceEntriesQuery(
    $id: String!
    $thresholdDate: ISODate!
    $limit: Int
  ) {
    profile: userByPrimaryIdentityId(primaryIdentityId: $id) {
      id
      upcomingRaceEntries: raceEntries(
        filters: { raceStartDate_gte: $thresholdDate }
        orderBy: raceStartDate_desc
        limit: $limit
      ) {
        id
        status
        bookingId
        numberOfParticipants
        information {
          key
          value
        }
        eventSummary {
          eventId
          title
          raceId
          raceStartDate
          raceTitle
          raceType
          submissionWindow {
            startDate
            endDate
          }
        }
        participants {
          firstName
          lastName
          primaryIdentityId
        }
      }
      completedRaceEntries: raceEntries(
        filters: { raceStartDate_lt: $thresholdDate }
        orderBy: raceStartDate_desc
        limit: $limit
      ) {
        id
        status
        bookingId
        numberOfParticipants
        information {
          key
          value
        }
        eventSummary {
          eventId
          title
          raceId
          raceStartDate
          raceTitle
          raceType
          submissionWindow {
            startDate
            endDate
          }
        }
        resultSubmissions {
          id
          finishingTime
          participantIndex
          raceId
          metrics {
            distance
          }
        }
        participants {
          firstName
          lastName
          primaryIdentityId
        }
      }
    }
  }
`;

export const UserRaceEntryByBookingIdQuery = gql`
  query UserRaceEntryByBookingIdQuery($bookingId: ID!, $eventId: Int) {
    raceEntry: raceEntryByBookingId(bookingId: $bookingId, eventId: $eventId) {
      id
      status
      bookingId
      bookingType
      numberOfParticipants
      information {
        key
        value
      }
      groupId
      eventSummary {
        eventId
        title
        raceId
        raceStartDate
        raceTitle
        raceType
        submissionWindow {
          startDate
          endDate
        }
      }
      participants {
        firstName
        lastName
        dob
        email
        gender
        address
        phone
        primaryIdentityId
      }
    }
  }
`;

export const UpdateUserRaceEntryInformationMutation = gql`
  mutation UpdateUserRaceEntryInformationMutation(
    $information: [RaceEntryInformationInput!]!
    $id: ID!
    $year: Int
  ) {
    updateRaceEntry(
      input: { id: $id, information: $information, year: $year }
    ) {
      ...RaceEntryFragment
    }
  }
  ${RaceEntryFragment}
`;

export const UpdateNotificationStatusesMutation = gql`
  mutation UpdateNotificationStatuses(
    $notificationIds: [String!]!
    $status: NotificationStatus!
  ) {
    updateNotificationStatuses(
      notificationIds: $notificationIds
      status: $status
    )
  }
`;

export const AssociateGuestContributionIdsMutation = gql`
  mutation AssociateGuestContributionIds($ids: [ID!]!) {
    claimContentContribution(ids: $ids)
  }
`;

const SavedParticipantsFragment = `
  id
  isPrimaryUser
  title
  firstName
  lastName
  dateOfBirth
  phone
  emailAddress
  address {
    __typename
    ...on SavedInlinedAddress {
      line1
      line2
      city
      country
      postcode
    }
  }
`;

export const SavedParticipantsQuery = gql`
  query SavedParticipantsQuery($primaryIdentityId: String!) {
    userByPrimaryIdentityId(primaryIdentityId: $primaryIdentityId) {
      id
      primaryIdentityId
      savedParticipants {
        ${SavedParticipantsFragment}
      }
    }
  }
`;

export const CreateOrUpdateSavedParticipantMutation = gql`
  mutation CreateOrUpdateSavedParticipantMutation($input: SavedParticipantInput!) {
    createOrUpdateSavedParticipant(input: $input) {
      ${SavedParticipantsFragment}
    }
  }
`;
